/**
 * How a payment was taken, written the way a person writes it.
 *
 * The values arrive as they are stored: Amelia's gateway names (`onSite`,
 * `wc`), the portal's own (`card_terminal`), and whatever a future gateway
 * calls itself. Printed raw, a report reads "on_site" and "card_terminal"
 * under a column headed "How", which is a machine talking.
 *
 * So the general rule is applied to everything — underscores and camel humps
 * become spaces, and the sentence takes a capital — and the short list below
 * covers the names that rule would get wrong. It is a short list on purpose:
 * an unknown gateway should come out tidy rather than come out untouched, so
 * the fallback is the rule and not the raw string.
 */

/**
 * The four ways this school is paid, spelled the way the school spells them.
 *
 * Cash and Stripe need nothing — the general rule already writes them the way
 * anybody would. The other two are stored under the names the booking flow
 * writes (`card_terminal`, `bank_transfer`) and are called something shorter in
 * the office, and the office is right: a row headed "Card terminal" is naming
 * the machine rather than the payment, and "Bank transfer" is a BACS payment
 * with more letters.
 *
 * Nothing else belongs in here. A name added for a way nobody is paid is a
 * vocabulary being invented rather than tidied.
 */
const KNOWN: Record<string, string> = {
  bacs: "BACS",
  bank_transfer: "BACS",
  card: "Card",
  card_terminal: "Card",
};

/** What to call a payment whose method was never recorded. */
const UNRECORDED = "Not recorded";

export function paymentMethodLabel(method: string | null | undefined): string {
  const raw = (method ?? "").trim();
  if (raw === "") return UNRECORDED;

  // Broken into words before anything is looked up, so that the three
  // spellings of one method — `card_terminal`, `card-terminal`, `cardTerminal`
  // — are one key rather than three entries above.
  const words = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/);

  const whole = KNOWN[words.join("_")];
  if (whole !== undefined) return whole;

  return words
    .map((word, index) => {
      // An acronym is one of the names above rather than anything in capitals:
      // "CASH" typed by somebody with caps lock on is not an acronym, and a
      // rule that guessed from the capitals would shout it back.
      const spelled = KNOWN[word];
      if (spelled !== undefined) return spelled;
      // Sentence case, not title case: "Apple pay" rather than "Apple Pay",
      // which is how the rest of the portal's labels are written.
      if (index > 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}
